import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api, apiError, MONTHS_FR } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Autocomplete } from "@/components/Autocomplete";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = (year, month) => ({
  concert: "", date: `${year}-${String(month).padStart(2, "0")}-01`,
  heure: "", salle: "", ville: "", prod: "", contrat: "", config: "",
  jauge: "", rpo: false, autre_solution: false, bb_prod: false,
  commentaires: "", autres_lyon: "", autres_clermont_montlucon: "",
  autres_st_etienne_roanne: "", autres: "", intermittent_ids: [],
});

export default function ConcertModal({ open, onOpenChange, concert, year, month, intermittents, onSaved }) {
  const [form, setForm] = useState(empty(year, month));
  const [saving, setSaving] = useState(false);
  const [sug, setSug] = useState({ concerts: [], salles: [], villes: [], prods: [], jauges_by_venue: {} });
  const editing = Boolean(concert);

  useEffect(() => {
    if (!open) return;
    if (concert) {
      const mm = String(concert.month).padStart(2, "0");
      const dd = concert.day ? String(concert.day).padStart(2, "0") : "01";
      setForm({
        ...empty(concert.year, concert.month),
        ...concert,
        date: `${concert.year}-${mm}-${dd}`,
        rpo: Boolean(concert.rpo),
        autre_solution: Boolean(concert.autre_solution),
        bb_prod: Boolean(concert.bb_prod),
      });
    } else {
      setForm(empty(year, month));
    }
    api.get("/suggestions").then(({ data }) => setSug(data)).catch(() => {});
  }, [open, concert, year, month]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInter = (id) =>
    setForm((f) => ({
      ...f,
      intermittent_ids: f.intermittent_ids.includes(id)
        ? f.intermittent_ids.filter((x) => x !== id)
        : [...f.intermittent_ids, id],
    }));

  const save = async () => {
    if (!form.concert.trim()) {
      toast.error("Le nom du concert est requis");
      return;
    }
    setSaving(true);
    let y = Number(year), mo = Number(month), d = null;
    if (form.date) {
      const [yy, mm, dd] = form.date.split("-").map(Number);
      if (yy && mm) { y = yy; mo = mm; d = dd || null; }
    }
    const payload = {
      concert: form.concert.trim(), year: y, month: mo, day: d,
      heure: form.heure, salle: form.salle, ville: form.ville, prod: form.prod,
      contrat: form.contrat, config: form.config, jauge: form.jauge,
      rpo: form.rpo ? "X" : "", autre_solution: !!form.autre_solution, bb_prod: !!form.bb_prod,
      commentaires: form.commentaires,
      autres_lyon: form.autres_lyon, autres_clermont_montlucon: form.autres_clermont_montlucon,
      autres_st_etienne_roanne: form.autres_st_etienne_roanne, autres: form.autres,
      intermittent_ids: form.intermittent_ids,
    };
    try {
      if (editing) await api.put(`/concerts/${concert.id}`, payload);
      else await api.post("/concerts", payload);
      toast.success(editing ? "Concert mis à jour" : "Concert ajouté");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const contratOptions = useMemo(() => {
    const base = ["PROD", "CO-PROD", "PL"];
    return form.contrat && !base.includes(form.contrat) ? [form.contrat, ...base] : base;
  }, [form.contrat]);

  const configOptions = useMemo(() => {
    const base = ["ASSIS NUMEROTE", "ASSIS NUMEROTE / DEBOUT", "ASSIS LIBRE"];
    return form.config && !base.includes(form.config) ? [form.config, ...base] : base;
  }, [form.config]);

  const jaugeOptions = useMemo(
    () => sug.jauges_by_venue[`${(form.salle || "").toUpperCase()}|||${(form.ville || "").toUpperCase()}`] || [],
    [sug, form.salle, form.ville]
  );

  const checkbox = (k, label) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <Checkbox checked={!!form[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`concert-field-${k}`} />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="concert-modal">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Modifier le concert" : "Nouveau concert"} — {MONTHS_FR[Number(form.month)]} {form.year}
          </DialogTitle>
          <DialogDescription>
            Renseignez les informations du concert et les régisseurs affectés.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Concert / Spectacle *</Label>
            <Autocomplete value={form.concert} onChange={(v) => set("concert", v)}
              options={sug.concerts} placeholder="Nom du concert / spectacle" testid="concert-field-concert" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="concert-field-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Heure</Label>
            <Input value={form.heure} onChange={(e) => set("heure", e.target.value)} placeholder="20h00" data-testid="concert-field-heure" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Salle</Label>
            <Autocomplete value={form.salle} onChange={(v) => set("salle", v)} options={sug.salles} testid="concert-field-salle" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Ville</Label>
            <Autocomplete value={form.ville} onChange={(v) => set("ville", v)} options={sug.villes} testid="concert-field-ville" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Prod nationale</Label>
            <Autocomplete value={form.prod} onChange={(v) => set("prod", v)} options={sug.prods} testid="concert-field-prod" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Contrat</Label>
            <Select value={form.contrat || undefined} onValueChange={(v) => set("contrat", v)}>
              <SelectTrigger data-testid="concert-field-contrat"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                {contratOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Config</Label>
            <Select value={form.config || undefined} onValueChange={(v) => set("config", v)}>
              <SelectTrigger data-testid="concert-field-config"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>
                {configOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Jauge</Label>
            <Autocomplete value={form.jauge} onChange={(v) => set("jauge", v)} options={jaugeOptions}
              placeholder={form.salle && form.ville ? "" : "Saisir salle & ville d'abord"} testid="concert-field-jauge" />
          </div>
          <div className="col-span-2 flex flex-wrap gap-6 pt-1">
            {checkbox("rpo", "RPO")}
            {checkbox("autre_solution", "L'AUTRE SOLUTION")}
            {checkbox("bb_prod", "BBPROD")}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Commentaires</Label>
            <Textarea
              value={form.commentaires ?? ""}
              onChange={(e) => set("commentaires", e.target.value)}
              rows={2}
              data-testid="concert-field-commentaires"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Autres Lyon</Label>
            <Input value={form.autres_lyon ?? ""} onChange={(e) => set("autres_lyon", e.target.value)} data-testid="concert-field-autres_lyon" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Autre Clermont / Montluçon</Label>
            <Input value={form.autres_clermont_montlucon ?? ""} onChange={(e) => set("autres_clermont_montlucon", e.target.value)} data-testid="concert-field-autres_clermont_montlucon" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Autres St Étienne / Roanne</Label>
            <Input value={form.autres_st_etienne_roanne ?? ""} onChange={(e) => set("autres_st_etienne_roanne", e.target.value)} data-testid="concert-field-autres_st_etienne_roanne" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Autres</Label>
            <Input value={form.autres ?? ""} onChange={(e) => set("autres", e.target.value)} data-testid="concert-field-autres" />
          </div>
        </div>

        <div className="mt-2">
          <Label className="text-xs uppercase tracking-wider text-primary">Régisseurs affectés</Label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {intermittents.map((i) => {
              const checked = form.intermittent_ids.includes(i.id);
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={i.id}
                  onClick={() => toggleInter(i.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleInter(i.id); } }}
                  data-testid={`concert-inter-toggle-${i.id}`}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors text-left cursor-pointer ${
                    checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"
                  }`}
                >
                  <Checkbox checked={checked} className="pointer-events-none" />
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ background: i.color }}
                  />
                  <span className="truncate">{i.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="concert-save-button"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
